(() => {
  if (!customElements.get('tone-purchase')) {
    class TonePurchase extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.form = this.querySelector('form[data-product-form]');
        this.select = this.form?.querySelector('[data-selling-plan]');
        if (!this.form || !this.select) return;
        this.plans = Array.from(this.select.options).filter((option) => option.value);
        const pills = this.querySelector('[data-plan-pills]');
        if (pills) {
          pills.replaceChildren(...this.plans.map((option) => {
            const label = document.createElement('label');
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = `_plan-${this.form.getAttribute('id')}`;
            input.value = option.value;
            input.dataset.planChoice = '';
            input.setAttribute('aria-label', `${option.dataset.name || option.dataset.label} — ${option.dataset.price}`);
            const text = document.createElement('span');
            text.textContent = option.dataset.label;
            label.append(input, text);
            return label;
          }));
        }
        this.querySelector('[data-native-delivery]').hidden = true;
        this.querySelector('[data-delivery-ui]').hidden = false;
        this.querySelectorAll('[data-quantity-step]').forEach((button) => { button.hidden = false; });
        this.addEventListener('change', (event) => this.change(event), { signal: this.controller.signal });
        this.addEventListener('click', (event) => this.click(event), { signal: this.controller.signal });
        this.update(false);
      }
      disconnectedCallback() { this.controller?.abort(); }
      change(event) {
        const target = event.target;
        if (target.matches('[data-delivery-mode]')) {
          this.select.value = target.value === 'once' ? '' : (this.lastPlan || this.plans[0]?.value || '');
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (target.matches('[data-plan-choice]')) {
          this.select.value = target.value;
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (target === this.select) {
          this.update(true);
        }
      }
      click(event) {
        const button = event.target.closest('[data-quantity-step]');
        if (!button || !this.contains(button)) return;
        const input = this.form.elements.quantity;
        if (!input || input.disabled) return;
        const step = Number(input.step) || 1;
        const minimum = Number(input.min) || 1;
        const maximum = input.max ? Number(input.max) : Infinity;
        const value = Number(input.value) || minimum;
        input.value = String(Math.min(maximum, Math.max(minimum, value + Number(button.dataset.quantityStep) * step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      renderPrice(target, option) {
        if (!target || !option) return;
        target.replaceChildren();
        if (option.dataset.compare) {
          const compare = document.createElement('s');
          compare.textContent = option.dataset.compare;
          target.append(compare);
        }
        const price = document.createElement('span');
        price.textContent = option.dataset.price;
        target.append(price);
      }
      update(announce) {
        const option = this.select.selectedOptions[0];
        const planId = option?.value || '';
        const repeat = Boolean(planId);
        if (repeat) this.lastPlan = planId;
        this.querySelectorAll('[data-delivery-mode]').forEach((radio) => { radio.checked = radio.value === (repeat ? 'repeat' : 'once'); });
        this.querySelectorAll('[data-plan-choice]').forEach((radio) => { radio.checked = radio.value === planId; });
        this.querySelectorAll('[data-delivery-row]').forEach((row) => { row.dataset.selected = String(row.dataset.deliveryRow === (repeat ? 'repeat' : 'once')); });
        const oncePrice = this.querySelector('[data-once-price]');
        const repeatPrice = this.querySelector('[data-repeat-price]');
        this.renderPrice(oncePrice, Array.from(this.select.options).find((item) => !item.value));
        this.renderPrice(repeatPrice, repeat ? option : this.plans.find((item) => item.value === this.lastPlan) || this.plans[0]);
        if (oncePrice) oncePrice.hidden = repeat;
        if (repeatPrice) repeatPrice.hidden = !repeat;
        const frequency = this.querySelector('[data-frequency]');
        if (frequency) frequency.hidden = !repeat;
        const saving = this.querySelector('[data-plan-saving]');
        if (saving) saving.textContent = repeat ? option?.dataset.savings || '' : '';
        this.querySelectorAll('[data-plan-detail]').forEach((detail) => {
          detail.hidden = detail.dataset.planDetail !== planId;
        });
        if (announce) this.dispatchEvent(new CustomEvent('tone:plan-changed', {
          bubbles: true, detail: { variantId: this.dataset.variantId, sellingPlanId: planId || null, label: option?.textContent || '' },
        }));
      }
    }
    customElements.define('tone-purchase', TonePurchase);
  }
  if (!customElements.get('tone-product')) {
    class ToneProduct extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-variant-link]');
          if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (window.ToneCart?.busy) return;
          this.syncVariantLinks();
          const url = new URL(link.href);
          if (!this.querySelector('[data-selling-plan]')?.value) url.searchParams.delete('selling_plan');
          this.load(url, true, link.dataset.variantLink);
        }, { signal: this.controller.signal });
        this.addEventListener('tone:plan-changed', (event) => {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', this.dataset.variantId);
          if (event.detail.sellingPlanId) url.searchParams.set('selling_plan', event.detail.sellingPlanId);
          else url.searchParams.delete('selling_plan');
          history.replaceState(history.state, '', url);
          this.syncVariantLinks();
          const status = this.querySelector('[data-variant-status]');
          if (status) status.textContent = event.detail.label;
        }, { signal: this.controller.signal });
        window.addEventListener('popstate', () => this.load(new URL(window.location.href), false), { signal: this.controller.signal });
        this.syncVariantLinks();
      }
      syncVariantLinks() {
        const plan = this.querySelector('[data-selling-plan]')?.value;
        this.querySelectorAll('[data-variant-link]').forEach((link) => {
          const url = new URL(link.href);
          let eligible;
          try { eligible = JSON.parse(link.dataset.variantPlans).map(String); } catch { eligible = plan ? [plan] : []; }
          const nextPlan = plan && (eligible.includes(plan) ? plan : eligible[0]);
          if (nextPlan) url.searchParams.set('selling_plan', nextPlan);
          else url.searchParams.delete('selling_plan');
          link.href = url;
        });
      }
      disconnectedCallback() { this.controller?.abort(); this.request?.abort(); }
      async load(destination, writeHistory, focusVariant) {
        this.request?.abort();
        const request = new AbortController();
        this.request = request;
        const timeout = setTimeout(() => request.abort(), 15000);
        const url = new URL(destination);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const quantity = this.querySelector('[name="quantity"]')?.value;
        const keepRepeat = Boolean(focusVariant && this.querySelector('[data-selling-plan]')?.value);
        const openDetails = new Set(Array.from(this.querySelectorAll('[data-preserve-detail][open]'), (detail) => detail.dataset.preserveDetail));
        this.setAttribute('aria-busy', 'true');
        this.querySelectorAll('[data-product-form] button, [data-product-form] input, [data-product-form] select').forEach((control) => { control.disabled = true; });
        try {
          const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('render');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = Array.from(html.querySelectorAll('tone-product')).find((element) => element.dataset.sectionId === this.dataset.sectionId);
          if (!next || request !== this.request) throw new Error('render');
          this.dataset.variantId = next.dataset.variantId;
          const appBlocks = new Map(Array.from(this.querySelectorAll('[data-commerce-app-block]'), (block) => [block.dataset.commerceAppBlock, block]));
          next.querySelectorAll('[data-commerce-app-block]').forEach((block) => {
            const existing = appBlocks.get(block.dataset.commerceAppBlock);
            if (existing) block.replaceWith(existing);
          });
          this.replaceChildren(...Array.from(next.childNodes));
          window.ToneCart?.syncServerState();
          if (window.ToneCart?.busy) window.ToneCart.lock(true);
          const purchase = this.querySelector('tone-purchase');
          const planSelect = this.querySelector('[data-selling-plan]');
          if (keepRepeat && planSelect && !planSelect.value) {
            const eligiblePlan = Array.from(planSelect.options).find((option) => option.value);
            if (eligiblePlan) {
              planSelect.value = eligiblePlan.value;
              purchase?.update(false);
            }
          }
          const quantityInput = this.querySelector('[name="quantity"]');
          if (quantityInput && quantity) {
            const minimum = Number(quantityInput.min) || 1;
            const increment = Number(quantityInput.step) || 1;
            const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
            const aligned = minimum + Math.max(0, Math.ceil((Number(quantity) - minimum) / increment)) * increment;
            quantityInput.value = String(Math.min(maximum, aligned));
          }
          this.querySelectorAll('[data-preserve-detail]').forEach((detail) => { detail.open = openDetails.has(detail.dataset.preserveDetail); });
          this.syncVariantLinks();
          const planId = this.querySelector('[data-selling-plan]')?.value;
          destination.searchParams.set('variant', this.dataset.variantId);
          if (planId) destination.searchParams.set('selling_plan', planId);
          else destination.searchParams.delete('selling_plan');
          if (writeHistory) history.pushState(history.state, '', destination);
          if (focusVariant) Array.from(this.querySelectorAll('[data-variant-link]')).find((link) => link.dataset.variantLink === focusVariant)?.focus({ preventScroll: true });
          const status = this.querySelector('[data-variant-status]');
          if (status) status.textContent = [this.querySelector('[data-variant-link][aria-current]')?.textContent.trim(), this.querySelector('[data-selling-plan]')?.selectedOptions[0]?.textContent].filter(Boolean).join(' — ');
          const detail = { variant: { id: this.dataset.variantId }, resource: { id: this.dataset.variantId }, productId: this.dataset.productId, sellingPlanId: planId || null, sectionId: this.dataset.sectionId };
          this.dispatchEvent(new CustomEvent('variant:update', { bubbles: true, detail }));
          this.dispatchEvent(new CustomEvent('variant:selected', { bubbles: true, detail }));
          document.dispatchEvent(new CustomEvent('tone:sections-rendered', { detail: { sections: [this.dataset.sectionId] } }));
        } catch (error) {
          if (request === this.request && this.isConnected) window.location.assign(destination);
        } finally {
          clearTimeout(timeout);
          if (request === this.request) this.removeAttribute('aria-busy');
        }
      }
    }
    customElements.define('tone-product', ToneProduct);
  }
})();
